/* MoG 2.1 — lightweight theme runtime. No dependencies.
   Video manager, rails, dialogs, cart feedback, delivery window.
   Round 1 (Oct 2026): the scroll reveal, parallax, rail image drift and the
   <zv-story> scroll routine were removed on purpose — nothing animates on scroll. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ZV = (window.ZV = window.ZV || {});
  var raf = window.requestAnimationFrame.bind(window);

  /* ---------------------------------------------------------------- reveal (static)
     The IntersectionObserver fade-in is gone. Any element that still carries
     [data-zv-reveal] (older sections, pages not touched in round 1) is marked
     .is-in at once so it can never stay hidden by a leftover CSS rule. */
  ZV.settleReveal = function (root) {
    var scope = root || document;
    scope.querySelectorAll('[data-zv-reveal]:not(.is-in)').forEach(function (el) {
      el.classList.add('is-in');
    });
  };

  /* ---------------------------------------------------------------- video
     Lazy-loaded, muted, looping. One autoplaying video at a time; paused when
     off-screen or the tab is hidden; never autoplays with reduced motion or Save-Data. */
  var currentVideo = null;
  var vidIO = null;
  function saveData() { return !!(navigator.connection && navigator.connection.saveData); }
  function pickSource(video) {
    var list = [];
    try { list = JSON.parse(video.getAttribute('data-sources') || '[]'); } catch (e) { list = []; }
    list = list.filter(function (s) { return (s.format || '').toLowerCase() === 'mp4' || /mp4/.test(s.mime_type || ''); });
    if (!list.length) return null;
    list.sort(function (a, b) { return a.width - b.width; });
    var want = Math.min(1280, (video.clientWidth || 640) * Math.min(2, window.devicePixelRatio || 1));
    for (var i = 0; i < list.length; i++) if (list[i].width >= want) return list[i].url;
    return list[list.length - 1].url;
  }
  function loadVideo(v) {
    if (v.__zvLoaded) return;
    var src = pickSource(v);
    if (!src) return;
    v.__zvLoaded = true;
    v.src = src;
    v.load();
  }
  function playVideo(v) {
    if (reduceMotion || saveData() || v.__zvUserPaused) return;
    if (!v.__zvLoaded) loadVideo(v);
    if (currentVideo && currentVideo !== v) currentVideo.pause();
    currentVideo = v;
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  function pauseVideo(v) {
    v.pause();
    if (currentVideo === v) currentVideo = null;
  }
  ZV.initVideos = function (root) {
    if (!('IntersectionObserver' in window)) return;
    if (!vidIO) {
      vidIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            var v = e.target;
            if (e.isIntersecting) {
              if (e.intersectionRatio > 0.35) playVideo(v);
              else if (!v.__zvLoaded && !saveData()) loadVideo(v);
            } else pauseVideo(v);
          });
        },
        { threshold: [0, 0.35], rootMargin: '120px 0px' }
      );
      document.addEventListener('visibilitychange', function () {
        if (document.hidden && currentVideo) currentVideo.pause();
      });
    }
    (root || document).querySelectorAll('video[data-zv-video]').forEach(function (v) {
      if (v.__zvInit) return;
      v.__zvInit = true;
      vidIO.observe(v);
    });
  };
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-zv-video-toggle]');
    if (!btn) return;
    var host = btn.closest('[data-zv-video-host]') || btn.parentElement;
    var v = host && host.querySelector('video[data-zv-video]');
    if (!v) return;
    if (v.paused) {
      v.__zvUserPaused = false;
      v.__zvForce = true;
      if (!v.__zvLoaded) loadVideo(v);
      if (currentVideo && currentVideo !== v) currentVideo.pause();
      currentVideo = v;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', btn.getAttribute('data-label-pause') || 'Pause video');
      btn.classList.remove('is-paused');
    } else {
      v.__zvUserPaused = true;
      v.pause();
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', btn.getAttribute('data-label-play') || 'Play video');
      btn.classList.add('is-paused');
    }
  });

  /* ---------------------------------------------------------------- dialogs */
  ZV.openDialog = function (dialog, trigger) {
    if (!dialog || dialog.open) return;
    dialog.__trigger = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('zv-no-scroll');
    void dialog.offsetWidth;
    dialog.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var focusTarget = dialog.querySelector('[data-zv-autofocus]');
    if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 60);
    /* showModal() focuses the first link; keep the logo from taking the initial focus ring */
    var initial = document.activeElement;
    if (initial && initial.classList && initial.classList.contains('zv-logo--menu')) {
      dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
  };

  ZV.closeDialog = function (dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-open');
    var finish = function () {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.documentElement.classList.remove('zv-no-scroll');
      var t = dialog.__trigger;
      if (t && t.setAttribute) {
        t.setAttribute('aria-expanded', 'false');
        t.focus({ preventScroll: true });
      }
    };
    if (reduceMotion) return finish();
    setTimeout(finish, 320);
  };

  ZV.bindDialog = function (dialog) {
    if (!dialog || dialog.__zvBound) return;
    dialog.__zvBound = true;
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      ZV.closeDialog(dialog);
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog || e.target.hasAttribute('data-zv-dialog-backdrop')) ZV.closeDialog(dialog);
      if (e.target.closest('[data-zv-close]')) ZV.closeDialog(dialog);
    });
  };

  /* ---------------------------------------------------------------- cart */
  ZV.addToCart = function (formData) {
    var drawer = document.querySelector('cart-drawer');
    var sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map(function (s) { return s.id; }) : [];
    if (sections.length) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (state.status) {
          var err = new Error(state.description || state.message || 'error');
          err.state = state;
          throw err;
        }
        if (drawer && state.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(state);
        } else window.location.href = window.routes.cart_url;
        document.dispatchEvent(new CustomEvent('zv:cart:added', { detail: state }));
        return state;
      });
  };

  /* Quick add: the '+' form on every product card (form[data-zv-quickadd]) opens the cart drawer */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-zv-quickadd]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('button');
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    ZV.addToCart(new FormData(form))
      .then(function () {
        btn.classList.add('is-added');
        setTimeout(function () { btn.classList.remove('is-added'); }, 1600);
      })
      .catch(function (err) {
        var live = document.getElementById('zv-live');
        if (live) live.textContent = err.message;
      })
      .then(function () { btn.removeAttribute('aria-busy'); });
  });

  /* Cart feedback: count bump + highlight of the line that was just added */
  function bumpCount() {
    var c = document.querySelector('.zv-cart-count');
    if (!c) return;
    c.classList.remove('is-bump');
    void c.offsetWidth;
    c.classList.add('is-bump');
  }
  document.addEventListener('zv:cart:added', function (e) {
    raf(function () {
      bumpCount();
      var id = e.detail && (e.detail.variant_id || e.detail.id);
      var match = id && document.querySelector('#CartDrawer [data-variant-id="' + id + '"], #CartDrawer [data-quantity-variant-id="' + id + '"]');
      var line = match ? match.closest('.cart-item') : document.querySelector('#CartDrawer .cart-item');
      if (line) {
        line.classList.add('is-new');
        setTimeout(function () { line.classList.remove('is-new'); }, 1800);
      }
    });
  });
  var lastCount = null;
  function watchCartCount() {
    var host = document.getElementById('cart-icon-bubble');
    if (!host || !('MutationObserver' in window)) return;
    var read = function () { var c = host.querySelector('.zv-cart-count'); return c ? c.textContent.trim() : '0'; };
    lastCount = read();
    new MutationObserver(function () {
      var now = read();
      if (now !== lastCount) { lastCount = now; bumpCount(); }
    }).observe(host, { childList: true, subtree: true, characterData: true });
  }

  /* ---------------------------------------------------------------- accordions
     Smooth closing for <details class="zv-acc__item"> (opening is CSS-only). */
  document.addEventListener('click', function (e) {
    var summary = e.target.closest('.zv-acc__item > summary, .zv-menu__group > summary, .zv-filters__group > summary');
    if (!summary || reduceMotion) return;
    var item = summary.parentElement;
    if (!item.open) return;
    var panel = item.querySelector('.zv-acc__panel');
    if (!panel) return;
    e.preventDefault();
    panel.style.gridTemplateRows = '0fr';
    setTimeout(function () {
      item.open = false;
      panel.style.gridTemplateRows = '';
    }, 420);
  });

  /* ---------------------------------------------------------------- rail
     <zv-rail>: scroll-snap track + prev/next arrows + progress bar + mouse drag.
     (The data-shift image drift was removed; the attribute is now ignored.) */
  if (!customElements.get('zv-rail')) {
    customElements.define(
      'zv-rail',
      class ZVRail extends HTMLElement {
        connectedCallback() {
          if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { this.init(); }.bind(this), { once: true });
          else this.init();
        }
        init() {
          if (this.__init) return;
          this.__init = true;
          this.track = this.querySelector('[data-zv-rail-track]');
          if (!this.track) return;
          this.prev = this.querySelector('[data-zv-rail-prev]');
          this.next = this.querySelector('[data-zv-rail-next]');
          this.bar = this.querySelector('[data-zv-rail-bar]');
          this.ticking = false;
          var self = this;
          if (this.prev) this.prev.addEventListener('click', function () { self.go(-1); });
          if (this.next) this.next.addEventListener('click', function () { self.go(1); });
          this.track.addEventListener('scroll', function () { self.schedule(); }, { passive: true });
          window.addEventListener('resize', function () { self.schedule(); });
          this.bindDrag();
          this.update();
        }
        go(dir) {
          var t = this.track;
          t.scrollBy({ left: dir * Math.max(240, t.clientWidth * 0.82), behavior: reduceMotion ? 'auto' : 'smooth' });
        }
        schedule() {
          if (this.ticking) return;
          this.ticking = true;
          raf(this.update.bind(this));
        }
        update() {
          this.ticking = false;
          var t = this.track;
          var max = t.scrollWidth - t.clientWidth;
          var pos = t.scrollLeft;
          if (this.prev) this.prev.disabled = pos <= 2;
          if (this.next) this.next.disabled = pos >= max - 2;
          var overflow = max > 4;
          this.classList.toggle('is-static', !overflow);
          if (this.bar) {
            var visible = t.clientWidth / t.scrollWidth;
            this.bar.style.width = Math.max(12, visible * 100) + '%';
            this.bar.style.transform = 'translateX(' + (max > 0 ? (pos / max) * (100 / visible - 100) : 0) + '%)';
          }
        }
        bindDrag() {
          var t = this.track;
          var down = false, startX = 0, startLeft = 0, moved = 0;
          t.addEventListener('pointerdown', function (e) {
            if (e.pointerType !== 'mouse' || e.button !== 0) return;
            down = true; moved = 0; startX = e.clientX; startLeft = t.scrollLeft;
          });
          window.addEventListener('pointermove', function (e) {
            if (!down) return;
            var dx = e.clientX - startX;
            moved = Math.max(moved, Math.abs(dx));
            if (moved > 6) {
              t.classList.add('is-dragging');
              t.scrollLeft = startLeft - dx;
            }
          });
          window.addEventListener('pointerup', function () {
            if (!down) return;
            down = false;
            setTimeout(function () { t.classList.remove('is-dragging'); }, 0);
          });
          t.addEventListener('click', function (e) {
            if (moved > 6) { e.preventDefault(); e.stopPropagation(); moved = 0; }
          }, true);
        }
      }
    );
  }

  /* ---------------------------------------------------------------- story (guard only)
     The scroll-driven <zv-story> routine was removed (owner decision, Oct 2026).
     This stub keeps any page that still renders the element from breaking: no
     scroll listener, no progress; the first step stays visible and the tabs
     simply switch steps on click. */
  if (!customElements.get('zv-story')) {
    customElements.define(
      'zv-story',
      class ZVStoryStub extends HTMLElement {
        connectedCallback() {
          if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { this.init(); }.bind(this), { once: true });
          else this.init();
        }
        init() {
          if (this.__init) return;
          this.__init = true;
          try {
            this.style.setProperty('--zv-p', '0');
            this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-zv-step]'));
            this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-zv-tab]'));
            if (!this.steps.length) return;
            var self = this;
            this.set(0);
            this.tabs.forEach(function (tab, i) {
              tab.addEventListener('click', function () { self.set(i); });
            });
          } catch (e) { /* never block the page */ }
        }
        set(i) {
          this.setAttribute('data-active', String(i));
          this.steps.forEach(function (s, n) {
            s.classList.toggle('is-active', n === i);
            s.classList.toggle('is-past', n < i);
          });
          this.tabs.forEach(function (t, n) {
            t.classList.toggle('is-active', n === i);
            t.classList.toggle('is-past', n < i);
            t.setAttribute('aria-current', n === i ? 'step' : 'false');
          });
        }
      }
    );
  }

  /* ---------------------------------------------------------------- slides
     <zv-slides>: vertical image transitions (hair section). Pauses off-screen. */
  if (!customElements.get('zv-slides')) {
    customElements.define(
      'zv-slides',
      class ZVSlides extends HTMLElement {
        connectedCallback() {
          if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { this.init(); }.bind(this), { once: true });
          else this.init();
        }
        init() {
          if (this.__init) return;
          this.__init = true;
          this.items = Array.prototype.slice.call(this.querySelectorAll('[data-zv-slide]'));
          this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-zv-slide-dot]'));
          if (this.items.length < 2) return;
          this.i = 0;
          var self = this;
          this.dots.forEach(function (d, n) { d.addEventListener('click', function () { self.go(n); self.restart(); }); });
          this.seen = false;
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(function (entries) {
              self.seen = entries[0].isIntersecting;
              if (self.seen) self.start(); else self.stop();
            }, { threshold: 0.3 }).observe(this);
          }
        }
        go(n) {
          this.i = (n + this.items.length) % this.items.length;
          var i = this.i;
          this.items.forEach(function (el, k) {
            el.classList.toggle('is-active', k === i);
            el.classList.toggle('is-before', k < i);
          });
          this.dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); d.setAttribute('aria-current', k === i ? 'true' : 'false'); });
        }
        start() {
          if (reduceMotion || this.timer) return;
          var self = this;
          this.timer = setInterval(function () { self.go(self.i + 1); }, 4800);
        }
        stop() { clearInterval(this.timer); this.timer = null; }
        restart() { this.stop(); if (this.seen) this.start(); }
      }
    );
  }

  /* ---------------------------------------------------------------- delivery window
     <span data-zv-delivery data-min-days="5" data-max-days="8">levertijd 5–8 werkdagen</span>
     becomes "verwachte bezorging di 27 okt – vr 30 okt": today + N WORKING days
     (Sat/Sun skipped), formatted in the page language (<html lang>, fallback nl-NL).
     Optional data-zv-delivery-label="…" overrides the prefix (pass the translated
     'zaveno.pdp.expected_delivery' string there); an empty label prints the dates only.
     The server-rendered text is left untouched on any error. */
  function addWorkingDays(start, n) {
    var d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    var left = Math.max(0, Math.round(n));
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) left -= 1;
    }
    return d;
  }
  function isoDate(d) {
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }
  function deliveryFormatter(lang) {
    var opts = { weekday: 'short', day: 'numeric', month: 'short' };
    if (!('Intl' in window) || typeof Intl.DateTimeFormat !== 'function') return null;
    try { return new Intl.DateTimeFormat(lang, opts); } catch (e) {}
    try { return new Intl.DateTimeFormat('nl-NL', opts); } catch (e) {}
    return null;
  }
  ZV.deliveryWindow = function (root) {
    var scope = root || document;
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    var els = scope.querySelectorAll('[data-zv-delivery]:not([data-zv-delivery-set])');
    if (!els.length) return;
    var lang = (document.documentElement.getAttribute('lang') || 'nl-NL').trim() || 'nl-NL';
    var fmt = deliveryFormatter(lang);
    if (!fmt) return;
    var today = new Date();
    Array.prototype.forEach.call(els, function (el) {
      try {
        var min = parseInt(el.getAttribute('data-min-days'), 10);
        var max = parseInt(el.getAttribute('data-max-days'), 10);
        if (isNaN(min) && isNaN(max)) return;
        if (isNaN(min)) min = max;
        if (isNaN(max)) max = min;
        if (max < min) { var tmp = min; min = max; max = tmp; }
        var from = addWorkingDays(today, min);
        var to = addWorkingDays(today, max);
        var a = fmt.format(from);
        var b = fmt.format(to);
        if (!a || !b) return;
        var range = a === b ? a : a + ' – ' + b;
        var label = el.getAttribute('data-zv-delivery-label');
        if (label === null) label = /^nl/i.test(lang) ? 'verwachte bezorging' : 'expected delivery';
        label = label.trim();
        var text = label ? label + ' ' + range : range;
        el.textContent = text;
        el.setAttribute('data-zv-delivery-from', isoDate(from));
        el.setAttribute('data-zv-delivery-to', isoDate(to));
        el.setAttribute('data-zv-delivery-set', '1');
      } catch (e) { /* keep the server text */ }
    });
  };
  /* Re-run after the cart drawer re-renders (Section Rendering API replaces its nodes). */
  function watchCartDrawerDelivery() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !('MutationObserver' in window)) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      raf(function () {
        pending = false;
        ZV.deliveryWindow(drawer);
      });
    }).observe(drawer, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------- boot */
  function boot(root) {
    ZV.settleReveal(root);
    ZV.initVideos(root);
    ZV.deliveryWindow(root);
  }
  document.addEventListener('DOMContentLoaded', function () {
    boot();
    watchCartCount();
    watchCartDrawerDelivery();
  });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('zv:cart:added', function () { raf(function () { ZV.deliveryWindow(); }); });
})();
